import React, { useState, useEffect } from 'react';
import TeamBrowser from './components/TeamBrowser';
import TeamDetail from './components/TeamDetail';
import SelectionAdvisor from './components/SelectionAdvisor';
import TypeChart from './components/TypeChart';
import MyTeams from './components/MyTeams';
import UserBadge from './components/UserBadge';
import KoFiButton from './components/KoFiButton';
import BugReportModal from './components/BugReportModal';
import AdminDashboard from './components/AdminDashboard';
import Logo from './Logo';
import { track, trackPage } from './analytics';
import { useIsAdmin } from './auth/useAdmin';
import './App.css';
import './components/AuthModal.css';

function App() {
  const [currentView, setCurrentView] = useState('teams');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [bugModalOpen, setBugModalOpen] = useState(false);
  const isAdmin = useIsAdmin();

  useEffect(() => { trackPage(currentView); }, [currentView]);

  const goTo = (view, source) => {
    track('nav_click', { to: view, from: currentView, source: source || 'nav' });
    setCurrentView(view);
  };

  const handleSelectTeam = (team) => {
    setSelectedTeam(team);
    setCurrentView('detail');
  };

  const handleBack = () => {
    setCurrentView('teams');
    setSelectedTeam(null);
  };

  const handleUseTeam = (team) => {
    setSelectedTeam(team);
    setCurrentView('advisor');
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1 onClick={() => { setSelectedTeam(null); goTo('teams', 'logo'); }}>
            <Logo size={42} />
            <span className="brand-text">
              <span className="brand-pk">PK</span> Champs <span className="brand-coach">Coach</span>
            </span>
          </h1>
          <nav className="header-nav">
            <button
              className={currentView === 'teams' || currentView === 'detail' ? 'active' : ''}
              onClick={() => { setSelectedTeam(null); goTo('teams', 'nav'); }}
            >
              Home
            </button>
            <button
              className={currentView === 'myteams' ? 'active' : ''}
              onClick={() => goTo('myteams', 'nav')}
            >
              My Teams
            </button>
            <button
              className={currentView === 'advisor' ? 'active' : ''}
              onClick={() => goTo('advisor', 'nav')}
            >
              Battle Advisor
            </button>
            <button
              className={currentView === 'typechart' ? 'active' : ''}
              onClick={() => goTo('typechart', 'nav')}
            >
              Type Chart
            </button>
            {isAdmin && (
              <button
                className={`header-admin-link ${currentView === 'admin' ? 'active' : ''}`}
                onClick={() => goTo('admin', 'nav')}
                title="Admin · bug reports"
              >
                Admin
              </button>
            )}
            <KoFiButton variant="compact" label="" />
            <UserBadge />
          </nav>
        </div>
      </header>

      <main className="app-main">
        {currentView === 'teams' && (
          <>
            <section className="hero">
              <div className="hero-content">
                <div className="hero-badge">Pokemon Champions — Reg M-A</div>
                <h2 className="hero-title">Your competitive edge starts here.</h2>
                <p className="hero-sub">
                  Build smarter teams, analyze every matchup, and climb the ladder with a coaching system designed for serious players.
                </p>
                <div className="hero-actions">
                  <button className="hero-btn primary" onClick={() => { track('hero_cta_click', { cta: 'build_team' }); goTo('myteams', 'hero'); }}>Build a Team</button>
                  <button className="hero-btn secondary" onClick={() => { track('hero_cta_click', { cta: 'open_advisor' }); goTo('advisor', 'hero'); }}>Open Battle Advisor</button>
                </div>
              </div>
              <div className="hero-visual">
                <div className="hero-sprites">
                  <img src={`${process.env.PUBLIC_URL}/sprites/garchomp.png`} alt="" className="hero-sprite s1" />
                  <img src={`${process.env.PUBLIC_URL}/sprites/froslass.png`} alt="" className="hero-sprite s2" />
                  <img src={`${process.env.PUBLIC_URL}/sprites/incineroar.png`} alt="" className="hero-sprite s3" />
                  <img src={`${process.env.PUBLIC_URL}/sprites/dragapult.png`} alt="" className="hero-sprite s4" />
                  <img src={`${process.env.PUBLIC_URL}/sprites/kingambit.png`} alt="" className="hero-sprite s5" />
                  <img src={`${process.env.PUBLIC_URL}/sprites/dragonite.png`} alt="" className="hero-sprite s6" />
                  <div className="hero-sprites-glow" />
                </div>
              </div>
            </section>

            <section className="features">
              <div className="feature-card">
                <div className="feature-icon">🛡</div>
                <h3>In-Depth Team Builder</h3>
                <p>Build teams with full movesets, stat points, abilities, items, and natures. See type coverage, defensive matchups, and team evaluation — all in real time as you build.</p>
                <button className="feature-link" onClick={() => { track('feature_card_click', { card: 'team_builder' }); goTo('myteams', 'feature'); }}>Start building →</button>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📊</div>
                <h3>Match History &amp; Metrics</h3>
                <p>Record what your opponents bring and lead with. The system learns from your matches and adjusts predictions — the more you play, the sharper it gets.</p>
                <button className="feature-link" onClick={() => { track('feature_card_click', { card: 'match_history' }); goTo('advisor', 'feature'); }}>Track matches →</button>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🎯</div>
                <h3>Battle Coaching System</h3>
                <p>Get turn-by-turn game plans, lead recommendations, damage calculations, and win condition analysis. Like having a coach in your ear during team preview.</p>
                <button className="feature-link" onClick={() => { track('feature_card_click', { card: 'coaching' }); goTo('advisor', 'feature'); }}>Get coached →</button>
              </div>
            </section>

            <section className="home-divider">
              <span>Browse Meta Teams</span>
            </section>

            <TeamBrowser onSelectTeam={handleSelectTeam} onUseInAdvisor={handleUseTeam} />
          </>
        )}
        {currentView === 'detail' && selectedTeam && (
          <TeamDetail team={selectedTeam} onBack={handleBack} onUseTeam={handleUseTeam} />
        )}
        {currentView === 'myteams' && (
          <MyTeams onUseInAdvisor={handleUseTeam} />
        )}
        {currentView === 'advisor' && (
          <SelectionAdvisor preselectedTeam={selectedTeam} onGoToMyTeams={() => setCurrentView('myteams')} />
        )}
        {currentView === 'typechart' && (
          <TypeChart />
        )}
        {currentView === 'admin' && (
          <AdminDashboard />
        )}
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <Logo size={28} />
            <div>
              <div className="footer-brand-name">PK Champs Coach</div>
              <div className="footer-brand-tagline">Competitive team builder &amp; match coach</div>
            </div>
          </div>
          <div className="footer-support">
            <p className="footer-support-copy">
              Love the tool? Help keep it free and growing.
            </p>
            <KoFiButton />
          </div>
        </div>
        <div className="footer-meta">
          <span>© {new Date().getFullYear()} PK Champs Coach</span>
          <span className="footer-dot">·</span>
          <span>Not affiliated with Nintendo, Game Freak, or The Pokémon Company.</span>
          <span className="footer-dot">·</span>
          <button
            type="button"
            className="footer-bug-btn"
            onClick={() => { track('bug_report_opened', { page: currentView }); setBugModalOpen(true); }}
            aria-label="Report a bug"
          >
            <span aria-hidden="true">🐛</span>
            <span>Report a bug</span>
          </button>
        </div>
      </footer>

      <BugReportModal
        open={bugModalOpen}
        onClose={() => setBugModalOpen(false)}
        currentView={currentView}
      />
    </div>
  );
}

export default App;
